import {
    Box,
    Typography,
    Avatar,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Pagination,
    Chip,
    TextField,
    InputAdornment,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";
import DummyUser from "../../Assets/User.webp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import Loader from "../../Components/Loader";

// PMF (Q1) answer -> chip styling. "Very Disappointed" is the GOOD outcome
// (they'd miss us), so it gets green.
const Q1_STYLES = {
    "Very Disappointed 😞": { bg: "#e8f5e9", fg: "#1b5e20", label: "Very disappointed" },
    "Somewhat Disappointed 😕": { bg: "#fff8e1", fg: "#8d6e00", label: "Somewhat disappointed" },
    "Not Disappointed 😐": { bg: "#fbe9e7", fg: "#a33c1d", label: "Not disappointed" },
};

const StatCard = ({ label, value, sub, accent }) => (
    <Box
        sx={{
            flex: "1 1 160px",
            background: "#fff",
            borderRadius: "10px",
            border: "1px solid #00000012",
            p: 2,
            borderTop: `3px solid ${accent || "#c6ff00"}`,
        }}
    >
        <Typography sx={{ fontSize: "13px", color: "#00000090" }}>{label}</Typography>
        <Typography sx={{ fontSize: "26px", fontWeight: 600, color: "#000", lineHeight: 1.2 }}>
            {value}
        </Typography>
        {sub && <Typography sx={{ fontSize: "12px", color: "#00000070" }}>{sub}</Typography>}
    </Box>
);

const Feedbacks = () => {
    const pageSize = 10;
    // Premium Users "PMF → Yes" links land here pre-filtered by email.
    const presetSearch = useLocation().state?.search || "";
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [feedbackData, setFeedbackData] = useState([]);
    const [totalData, setTotalData] = useState(0);
    const [summary, setSummary] = useState(null);
    const [search, setSearch] = useState(presetSearch);
    const [searchDraft, setSearchDraft] = useState(presetSearch);
    const [q1Filter, setQ1Filter] = useState(""); // "" | very | somewhat | not
    const [sort, setSort] = useState("newest"); // newest | oldest
    const [error, setError] = useState(null);
    // Controlled accordion expansion: individual toggling plus an
    // expand-all/collapse-all control.
    const [expandedIds, setExpandedIds] = useState(new Set());
    const allExpanded =
        feedbackData.length > 0 &&
        feedbackData.every((f) => expandedIds.has(f.id));
    const toggleCard = (id) =>
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    const toggleAll = () =>
        setExpandedIds(
            allExpanded ? new Set() : new Set(feedbackData.map((f) => f.id))
        );

    const getFeedbackList = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await AxiosInstance.get(ApiList.FEEDBACKS, {
                params: {
                    count: (page - 1) * pageSize,
                    limit: pageSize,
                    sort,
                    ...(search ? { search } : {}),
                    ...(q1Filter ? { q1: q1Filter } : {}),
                },
            });
            if (response?.data?.s === 1) {
                setFeedbackData(response?.data?.r || []);
                setTotalData(response?.data?.c || 0);
                if (response?.data?.summary) setSummary(response.data.summary);
            } else {
                setFeedbackData([]);
                setTotalData(0);
                setError(response?.data?.m || "Failed to load feedback");
            }
        } catch (e) {
            console.error("Error fetching feedback data:", e);
            setFeedbackData([]);
            setTotalData(0);
            setError("Could not reach the server");
        }
        setLoading(false);
    };

    useEffect(() => {
        getFeedbackList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, search, q1Filter, sort]);

    const submitSearch = () => {
        setPage(1);
        setSearch(searchDraft.trim());
    };

    // Derive the PMF summary numbers from the q1_split rows.
    const splitFor = (key) =>
        summary?.q1_split?.find((s) => s.answer_text === key)?.n || 0;
    const q1Total =
        (summary?.q1_split || []).reduce((acc, s) => acc + (s.n || 0), 0) || 0;
    const veryN = splitFor("Very Disappointed 😞");
    const pmfPct = q1Total > 0 ? Math.round((veryN / q1Total) * 100) : null;

    const q1For = (feedback) =>
        feedback?.feedbacks?.find((qa) => qa.question_number === 1)?.answer_text;

    return (
        <Box sx={{ width: "100%", height: "100vh", background: "#B9CC0310", overflow: "hidden" }}>
            <Box
                sx={{
                    width: "100%",
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                <Typography sx={{ fontSize: { xs: "18px", md: "28px" }, fontWeight: "300", color: "#000" }}>
                    Feedbacks
                </Typography>
                <TextField
                    size="small"
                    placeholder="Search name, username, or email"
                    value={searchDraft}
                    onChange={(e) => setSearchDraft(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submitSearch()}
                    onBlur={submitSearch}
                    sx={{ background: "#fff", borderRadius: "8px", minWidth: "280px" }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: "#00000060" }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Box sx={{ width: "100%", height: "calc(100vh - 74px)" }}>
                <Box sx={{ width: "100%", height: "100%", p: 2, pt: 0, overflow: "auto" }}>
                    {/* Summary row */}
                    {summary && (
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                            <StatCard
                                label="Unique responders"
                                value={summary.unique_responders}
                                sub={`${totalData} surveys${search ? " (filtered)" : ""}`}
                            />
                            <StatCard
                                label="PMF score (very disappointed)"
                                value={pmfPct !== null ? `${pmfPct}%` : "—"}
                                sub="benchmark: 40%+"
                                accent={pmfPct !== null && pmfPct >= 40 ? "#2e7d32" : "#e65100"}
                            />
                            <StatCard
                                label="Somewhat disappointed"
                                value={splitFor("Somewhat Disappointed 😕")}
                                accent="#f9a825"
                            />
                            <StatCard
                                label="Not disappointed"
                                value={splitFor("Not Disappointed 😐")}
                                accent="#bf360c"
                            />
                        </Box>
                    )}

                    {/* Filter row */}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
                        {[
                            { key: "", label: "All" },
                            { key: "very", label: "Very disappointed" },
                            { key: "somewhat", label: "Somewhat disappointed" },
                            { key: "not", label: "Not disappointed" },
                        ].map((f) => (
                            <Chip
                                key={f.key || "all"}
                                label={f.label}
                                onClick={() => {
                                    setPage(1);
                                    setQ1Filter(f.key);
                                }}
                                sx={{
                                    cursor: "pointer",
                                    fontWeight: q1Filter === f.key ? 600 : 400,
                                    background: q1Filter === f.key ? "#c6ff00" : "#fff",
                                    border: "1px solid #00000020",
                                    "&:hover": { background: q1Filter === f.key ? "#c6ff00" : "#f2f6d9" },
                                }}
                            />
                        ))}
                        <Box sx={{ flex: 1 }} />
                        <Chip
                            label={allExpanded ? "Collapse all" : "Expand all"}
                            onClick={toggleAll}
                            sx={{
                                cursor: "pointer",
                                background: "#fff",
                                border: "1px solid #00000020",
                                "&:hover": { background: "#f2f6d9" },
                            }}
                        />
                        <Chip
                            label={sort === "newest" ? "Newest first" : "Oldest first"}
                            onClick={() => {
                                setPage(1);
                                setSort(sort === "newest" ? "oldest" : "newest");
                            }}
                            sx={{
                                cursor: "pointer",
                                background: "#fff",
                                border: "1px solid #00000020",
                                "&:hover": { background: "#f2f6d9" },
                            }}
                        />
                    </Box>

                    {loading ? (
                        <Box sx={{ width: "100%", height: "400px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <Loader />
                        </Box>
                    ) : feedbackData.length === 0 ? (
                        <Box sx={{ width: "100%", height: "300px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <Typography sx={{ color: "#00000080", fontSize: "16px" }}>
                                {error || (search ? `No feedback matches "${search}"` : "No feedback yet")}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pb: 2 }}>
                            {feedbackData.map((feedback, index) => {
                                const q1 = q1For(feedback);
                                const q1Style = Q1_STYLES[q1];
                                const displayName =
                                    feedback?.fname || feedback?.username || "Anonymous User";
                                const respondedAt =
                                    feedback?.responded_at || feedback?.feedbacks?.[0]?.created_at;
                                return (
                                    <Accordion
                                        key={feedback.id || index}
                                        expanded={expandedIds.has(feedback.id)}
                                        onChange={() => toggleCard(feedback.id)}
                                        sx={{
                                            background: "#fff",
                                            borderRadius: "10px !important",
                                            border: "1px solid #00000010",
                                            boxShadow: "none",
                                            "&:before": { display: "none" },
                                            "&.Mui-expanded": { margin: "0 !important" },
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ color: "#7a8a00" }} />}
                                            sx={{
                                                minHeight: "64px !important",
                                                "&.Mui-expanded": { minHeight: "64px !important" },
                                                "& .MuiAccordionSummary-content": { margin: "10px 0 !important" },
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%", pr: 2 }}>
                                                <Avatar sx={{ width: 44, height: 44, border: "2px solid #c6ff00" }}>
                                                    <img
                                                        src={
                                                            feedback?.image
                                                                ? `${ApiList?.IMG_URL}${feedback.image}`
                                                                : DummyUser
                                                        }
                                                        alt={displayName}
                                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                    />
                                                </Avatar>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography sx={{ color: "#000", fontSize: "16px", fontWeight: 500 }} noWrap>
                                                        {displayName}
                                                        {feedback?.username && feedback?.fname && (
                                                            <Typography component="span" sx={{ color: "#00000070", fontSize: "13px", ml: 1 }}>
                                                                @{feedback.username}
                                                            </Typography>
                                                        )}
                                                    </Typography>
                                                    <Typography sx={{ color: "#00000080", fontSize: "13px" }} noWrap>
                                                        {feedback?.email || "No email"}
                                                    </Typography>
                                                    <Typography sx={{ color: "#00000090", fontSize: "12px", mt: 0.25 }} noWrap>
                                                        {feedback?.total_walks ?? 0} walks
                                                        {" · "}
                                                        {feedback?.dog_count ?? 0} {feedback?.dog_count === 1 ? "dog" : "dogs"}
                                                        {feedback?.member_since &&
                                                            ` · joined ${new Date(feedback.member_since).toLocaleDateString(undefined, { month: "short", year: "numeric" })}`}
                                                    </Typography>
                                                </Box>
                                                {!!feedback?.is_premium && (
                                                    <Chip
                                                        label="Premium"
                                                        size="small"
                                                        sx={{ background: "#fff3cd", color: "#7a5c00", fontWeight: 600 }}
                                                    />
                                                )}
                                                {q1Style && (
                                                    <Chip
                                                        label={q1Style.label}
                                                        size="small"
                                                        sx={{
                                                            background: q1Style.bg,
                                                            color: q1Style.fg,
                                                            fontWeight: 500,
                                                        }}
                                                    />
                                                )}
                                                {respondedAt && (
                                                    <Typography sx={{ color: "#00000080", fontSize: "13px", whiteSpace: "nowrap" }}>
                                                        {new Date(respondedAt).toLocaleDateString()}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </AccordionSummary>

                                        <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                                                {feedback?.feedbacks?.map((qa, qaIndex) => (
                                                    <Box
                                                        key={qaIndex}
                                                        sx={{
                                                            background: "#B9CC0318",
                                                            borderRadius: "8px",
                                                            p: 1.5,
                                                            borderLeft: "3px solid #9eb611",
                                                        }}
                                                    >
                                                        <Typography sx={{ color: "#00000090", fontSize: "13px", fontWeight: 500, mb: 0.5 }}>
                                                            Q{qa.question_number}. {qa.question_text || `Question ${qa.question_number}`}
                                                        </Typography>
                                                        <Typography sx={{ color: "#000", fontSize: "14px", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                                                            {qa.answer_text}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}

                            {totalData > pageSize && (
                                <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2 }}>
                                    <Pagination
                                        count={Math.ceil(totalData / pageSize)}
                                        page={page}
                                        onChange={(e, value) => setPage(value)}
                                        sx={{
                                            "& .MuiPaginationItem-root.Mui-selected": {
                                                background: "#c6ff00",
                                                color: "black",
                                                fontWeight: "600",
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default Feedbacks;
